import {
  type ProspectTaskDto,
  type TaskCreateDto,
  useSalesCompleteTask,
  useSalesCreateTask,
  useSalesDeleteTask,
  useSalesUpdateTask,
} from "@crowdcoursing/api/openapi";
import { DateTime } from "luxon";
import { useState } from "react";

import { useToast } from "~hooks/useToast";

export interface UseTaskModalOptions {
  /** Called after a successful sales task update (e.g. refresh parent lists). */
  onTaskUpdated?: (payload: { body: TaskCreateDto; taskId: string }) => void;
}

export const useTaskModal = (prospectId: string, options?: UseTaskModalOptions) => {
  const toast = useToast();
  const { mutateAsync: createTask } = useSalesCreateTask();
  const { mutateAsync: updateTask } = useSalesUpdateTask();
  const { mutateAsync: deleteTask } = useSalesDeleteTask();
  const { mutateAsync: completeTask } = useSalesCompleteTask();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editTask, setEditTask] = useState<ProspectTaskDto | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<ProspectTaskDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleCreateTask = async (values: TaskCreateDto) => {
    try {
      await createTask({
        body: values,
        pathParams: { id: prospectId },
      });
      setIsAddOpen(false);
      toast.success("Task created successfully");
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    }
  };

  const handleUpdateTask = async (values: TaskCreateDto) => {
    if (!editTask?.id) {
      return;
    }

    try {
      await updateTask({
        body: values,
        pathParams: { taskId: editTask.id },
      });
      options?.onTaskUpdated?.({ body: values, taskId: editTask.id });
      setEditTask(null);
      toast.success("Task updated successfully");
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await completeTask({
        pathParams: { taskId },
      });
      toast.success("Task completed");
    } catch (error) {
      console.error("Error completing task:", error);
      toast.error("Failed to complete task");
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete?.id) {
      return;
    }

    try {
      await deleteTask({
        pathParams: { taskId: taskToDelete.id },
      });
      setTaskToDelete(null);
      setDeleteConfirmOpen(false);
      toast.success("Task deleted");
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const handleSnoozeTask = async (taskId: string, task: ProspectTaskDto) => {
    try {
      // Snooze for 1 day, preserving all other task data
      const newDueDate = DateTime.now().plus({ days: 1 }).toISO();
      await updateTask({
        body: {
          assignedToUserId: task.assignedToUserId,
          contactId: task.contactId,
          description: task.description,
          dueAt: newDueDate,
          priority: task.priority,
          reminderAt: task.reminderAt,
          status: task.status,
          title: task.title,
        },
        pathParams: { taskId },
      });
      toast.success("Task snoozed for 1 day");
    } catch (error) {
      console.error("Error snoozing task:", error);
      toast.error("Failed to snooze task");
    }
  };

  const openAddModal = () => setIsAddOpen(true);
  const closeAddModal = () => setIsAddOpen(false);
  const openEditModal = (task: ProspectTaskDto) => setEditTask(task);
  const closeEditModal = () => setEditTask(null);
  const openDeleteModal = (task: ProspectTaskDto) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteConfirmOpen(false);
    setTaskToDelete(null);
  };

  return {
    closeAddModal,
    closeDeleteModal,
    closeEditModal,
    deleteConfirmOpen,
    editTask,
    handleCompleteTask,
    // Task actions
    handleCreateTask,
    handleDeleteTask,
    handleSnoozeTask,
    handleUpdateTask,

    // Modal states
    isAddOpen,

    // Modal actions
    openAddModal,
    openDeleteModal,
    openEditModal,
    taskToDelete,
  };
};
